import { loadWasm } from 'necjs';
import config from '../web3/web3Config.json';
import { getDmailSymmetricKey, isApiError, saveDmailSymmetricKey } from '../service/api-actions';
import { getValidAccessToken } from '../auth/tokenGuard';

/**
 * dekManager.js — dMail data-encryption-key (DEK) envelope.
 *
 * dMail owns ONE symmetric DEK (app_id "dmail", via /dmail/symmetric-key). It is wrapped
 * (asymmetrically encrypted) to the user's OWN chain encryption public key and stored server-side, so
 * only the NCOG wallet can unwrap it. Per session the wallet unwraps it EXACTLY ONCE (ncog_decrypt);
 * every later encrypt/decrypt is local symmetric crypto — O(1) wallet ops per session instead of
 * O(n) per mail.
 *
 * This is dMail's OWN key and is entirely separate from the dContacts DEK resolved by
 * ../helper/contactsDek.js (different app_id, different endpoints, different key material).
 * Mail must never be encrypted under the contacts key, or vice versa.
 *
 * Ported from dcalendar/src/helper/dekManager.ts. Invariants that MUST hold (getting these wrong
 * silently loses data):
 *  - necjs encrypt()/symEncrypt() return OBJECTS { encryptedData, version }; decrypt()/symDecrypt()
 *    return STRINGS. We unwrap `.encryptedData` at the boundary so callers only see strings.
 *  - The WASM/RPC `version` is ALWAYS 'v1'. 'v2' is a per-ROW / per-cache discriminator, NOT a crypto
 *    version — it must never be passed to the WASM.
 *  - ncog_decrypt's param is `cypherText` (suite-wide), NOT `encryptedData`.
 *  - The wallet NUL-pads what it returns; the padding MUST be stripped or the key string differs.
 *  - Never overwrite an existing wrapped DEK: an overwrite permanently orphans everything encrypted
 *    under the old one. Mint only when the server genuinely has none.
 *
 * The unwrapped DEK lives in module memory ONLY — never in localStorage/IndexedDB. A persisted DEK
 * outlives the session and is a plaintext key sitting next to the ciphertext it opens.
 */

// Built at runtime rather than written as a regex escape — see contactsDek.js for the same pattern.
const NUL = String.fromCharCode(0);

let _dek = null;      // session-only, in memory; NEVER persisted or logged
let _dekUser = null;
let _inflight = null;

function stripNulPadding(value) {
  return String(value).split(NUL).join('');
}

/**
 * Strip NUL padding and surrounding whitespace from a decrypted string.
 *
 * Exported for the mail call sites, which must sanitise symDecrypt output exactly as they already
 * sanitise ncog_decrypt output — the padding is an artefact of the crypto layer, not of the wallet, so a
 * locally-decrypted body needs the same treatment before it will JSON.parse. Written with
 * String.fromCharCode(0) rather than a regex escape on purpose (see the NUL constant above).
 */
export function stripNul(value) {
  return value == null ? '' : stripNulPadding(value).trim();
}

function currentUser(userName) {
  if (userName) return userName;
  try {
    return localStorage.getItem('UserName') || '';
  } catch {
    return '';
  }
}

function currentToken() {
  try {
    return getValidAccessToken() || '';
  } catch {
    return '';
  }
}

/** 32-byte random DEK, hex-encoded. */
export function mintDekHex() {
  const b = new Uint8Array(32);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

/** Local symmetric encrypt under the DEK. Returns the ciphertext string only. */
export async function symEncrypt(dekHex, plaintext) {
  const Mlkem = await loadWasm();
  const out = await Mlkem.symEncrypt(dekHex, plaintext); // { encryptedData, version:'v1' }
  return out?.encryptedData ?? out;
}

/** Local symmetric decrypt under the DEK. Returns the plaintext string. */
export async function symDecrypt(dekHex, ciphertext) {
  const Mlkem = await loadWasm();
  return await Mlkem.symDecrypt(dekHex, ciphertext, 'v1'); // 'v1' is required by the WASM layer
}

async function getOwnPubKeyHex() {
  const res = await window.ncogProvider.request({ method: 'ncog_encryptionPublicKey' });
  const pub = res?.response;
  if (!pub) throw new Error('dekManager: could not read the wallet encryption public key');
  return pub;
}

/**
 * Wrap the DEK to the user's own chain public key. Returns the { encryptedData, version } object the
 * save endpoint expects (the backend stores just the encryptedData string).
 */
async function wrapDekToSelf(dekHex) {
  const Mlkem = await loadWasm();
  const pub = await getOwnPubKeyHex();
  const out = await Mlkem.encrypt(pub, dekHex); // { encryptedData, version:'v1' }
  return { encryptedData: out?.encryptedData ?? out, version: out?.version ?? 'v1' };
}

/** Unwrap the wrapped DEK inside the wallet (the private key never reaches the page). One wallet call. */
async function walletUnwrapDek(wrapped) {
  const res = await window.ncogProvider.request({
    method: 'ncog_decrypt',
    params: { cypherText: wrapped, version: 'v1' },
  });
  if (!res || res.success === false || !res.response) {
    throw new Error('ncog_decrypt failed to unwrap the dMail DEK');
  }
  return stripNulPadding(res.response);
}

/**
 * Read the wrapped DEK from the server.
 *
 * Returns the wrapped blob, or null ONLY when the server authoritatively answered "no key yet".
 * Throws when the answer is not trustworthy. This distinction is load-bearing: api-actions' fetchAPI
 * swallows network/401/429 failures and returns its failure sentinel, which would otherwise read as
 * "no key" and make getSessionDEK mint a SECOND, WRONG DEK — orphaning every mail already cached
 * under the first. isApiError() is the check that catches that sentinel; the Array.isArray() test is
 * kept because fetchAPI used to signal failure with a bare [].
 */
async function fetchWrappedDek(userName) {
  const res = await getDmailSymmetricKey({ userName });
  if (!res || isApiError(res) || Array.isArray(res) || res.success === false) {
    throw new Error('dekManager: could not read the dMail DEK (request failed)');
  }
  const key = res?.data?.encrypted_key;
  return key ? String(key) : null;
}

async function saveWrappedDek(userName, wrapped) {
  const res = await saveDmailSymmetricKey(userName, wrapped);
  // A save that never reached the server used to be invisible (fetchAPI returned []), so this session
  // would go on sealing mail under a DEK that exists nowhere but this tab — unreadable everywhere,
  // forever. Fail loudly instead: the caller can retry once connectivity/auth is back.
  if (isApiError(res)) {
    throw new Error('dekManager: could not save the dMail DEK (request failed)');
  }
}

/**
 * Return this session's dMail DEK. Unwraps the server-stored wrapped DEK ONCE via the wallet, or — on
 * first-ever use — mints a fresh DEK, wraps it to the wallet and saves it. Cached in memory per user.
 * Concurrent callers are de-duplicated so a first-use race cannot mint two DEKs.
 */
export async function getSessionDEK(userName) {
  const user = currentUser(userName);
  if (!user) throw new Error('dekManager: no userName available');
  if (_dek && _dekUser === user) return _dek;
  if (_inflight && _inflight.user === user) return _inflight.p;

  const p = (async () => {
    const wrapped = await fetchWrappedDek(user);
    let dek;
    if (wrapped) {
      dek = await walletUnwrapDek(wrapped);
    } else {
      // Do NOT mint while unauthenticated. An empty token right after a reload / account switch makes
      // the read above look like "no key" for a user who actually has one; minting then would replace
      // this session's view of the key and orphan everything already encrypted. Require a token and let
      // the caller retry once auth has hydrated — an authenticated 200-without-key is the only real mint.
      if (!currentToken()) {
        throw new Error('dekManager: auth token not ready — cannot initialize the DEK yet');
      }
      dek = mintDekHex();
      const w = await wrapDekToSelf(dek);
      await saveWrappedDek(user, w);
      // Defense-in-depth for a concurrent first-mint in another tab/device: the server store is
      // write-once (a later INSERT is a no-op and the endpoint returns the EXISTING row), so re-read and
      // ADOPT whatever actually persisted. Runs once per user ever, and makes racing sessions converge
      // on one DEK instead of each trusting a key the server never kept.
      try {
        const confirmWrapped = await fetchWrappedDek(user);
        if (confirmWrapped) dek = await walletUnwrapDek(confirmWrapped);
      } catch {
        /* keep the just-minted DEK if the confirming read/unwrap fails */
      }
    }
    _dek = dek;
    _dekUser = user;
    return dek;
  })();

  _inflight = { user, p };
  try {
    return await p;
  } finally {
    if (_inflight && _inflight.p === p) _inflight = null;
  }
}

/**
 * Best-effort variant for callers that can degrade instead of failing: returns the DEK, or null if it
 * cannot be resolved (auth not ready, endpoint unavailable, wallet refused). Callers MUST treat null as
 * "skip the encrypted cache and use the wallet path" — never as "store it in plaintext instead".
 */
export async function tryGetSessionDEK(userName) {
  try {
    return await getSessionDEK(userName);
  } catch (error) {
    console.warn('dekManager: DEK unavailable —', error?.message || error);
    return null;
  }
}

/** Drop the cached DEK. Call on logout and on account switch. */
export function clearDekCache() {
  _dek = null;
  _dekUser = null;
  _inflight = null;
}

/**
 * True when the DEK write cutover is enabled. Default OFF so no v2 rows are produced until every client
 * ships the dual-read. This gates the OUTBOUND/server-side mail format only — the local at-rest cache
 * encryption in db/indexed-helper.js is unconditional and does not consult this flag.
 */
export function isDekWriteEnabled() {
  return config.DMAIL_DEK_WRITE === true;
}
